import 'dayjs/locale/ar';
import './App.css';

import {
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from 'react';
import { useLocation, useNavigate, useRoutes } from 'react-router-dom';

import { TestConnectionError } from '@/components/dev/TestConnectionError';
import ErrorBoundary from '@/components/ErrorBoundary';
import { UpdatePrompt } from '@/components/UpdatePrompt';
import { VersionCheck } from '@/components/VersionCheck';
import { VersionNotification } from '@/components/VersionNotification';
import { AppLoading } from '@/components/ui/AppLoading';
import { Toaster } from '@/components/ui/sonner';
import { ConnectionProvider } from '@/context/ConnectionContext';
import { useInitialLoadingContext } from '@/context/InitialLoadingContext';
import { RtlProvider } from '@/context/RtlContext';
import { useAriaHiddenFix } from '@/hooks/useAriaHiddenFix';
import router from '@/routes';
import { lazyLoad } from '@/utils/lazyLoad';
import {
  markPerformance,
  measurePerformance,
} from '@/utils/performanceMonitor';

// ===============================
// Constants
// ===============================

const TOAST_CLASSES = {
  base: 'group toast group-[.toaster]:bg-white/95 group-[.toaster]:backdrop-blur-md group-[.toaster]:border group-[.toaster]:border-gray-200/50 group-[.toaster]:shadow-lg group-[.toaster]:shadow-gray-200/50 dark:group-[.toaster]:bg-gray-900/95 dark:group-[.toaster]:border-gray-700/50 dark:group-[.toaster]:shadow-gray-900/50 group-[.toaster]:rounded-xl group-[.toaster]:p-4 group-[.toaster]:min-w-[320px] group-[.toaster]:max-w-[420px] font-tajawal',
  title:
    'text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1 font-tajawal',
  description:
    'text-xs text-gray-600 dark:text-gray-300 leading-relaxed font-tajawal',
  icon: 'flex-shrink-0 w-5 h-5',
  success:
    'group-[.toaster]:bg-gradient-to-r group-[.toaster]:from-green-50/95 group-[.toaster]:to-emerald-50/95 group-[.toaster]:border-green-200/60 group-[.toaster]:shadow-green-100/30 dark:group-[.toaster]:from-green-950/95 dark:group-[.toaster]:to-emerald-950/95 dark:group-[.toaster]:border-green-800/60 dark:group-[.toaster]:shadow-green-900/30 [&>[data-icon]]:text-green-600 dark:[&>[data-icon]]:text-green-400 [&>[data-icon]]:bg-green-100 dark:[&>[data-icon]]:bg-green-900/50 [&>[data-icon]]:p-1.5 [&>[data-icon]]:rounded-lg',
  error:
    'group-[.toaster]:bg-gradient-to-r group-[.toaster]:from-red-50/95 group-[.toaster]:to-rose-50/95 group-[.toaster]:border-red-200/60 group-[.toaster]:shadow-red-100/30 dark:group-[.toaster]:from-red-950/95 dark:group-[.toaster]:to-rose-950/95 dark:group-[.toaster]:border-red-800/60 dark:group-[.toaster]:shadow-red-900/30 [&>[data-icon]]:text-red-600 dark:[&>[data-icon]]:text-red-400 [&>[data-icon]]:bg-red-100 dark:[&>[data-icon]]:bg-red-900/50 [&>[data-icon]]:p-1.5 [&>[data-icon]]:rounded-lg',
  info: 'group-[.toaster]:bg-gradient-to-r group-[.toaster]:from-blue-50/95 group-[.toaster]:to-sky-50/95 group-[.toaster]:border-blue-200/60 group-[.toaster]:shadow-blue-100/30 dark:group-[.toaster]:from-blue-950/95 dark:group-[.toaster]:to-sky-950/95 dark:group-[.toaster]:border-blue-800/60 dark:group-[.toaster]:shadow-blue-900/30 [&>[data-icon]]:text-blue-600 dark:[&>[data-icon]]:text-blue-400 [&>[data-icon]]:bg-blue-100 dark:[&>[data-icon]]:bg-blue-900/50 [&>[data-icon]]:p-1.5 [&>[data-icon]]:rounded-lg',
  warning:
    'group-[.toaster]:bg-gradient-to-r group-[.toaster]:from-amber-50/95 group-[.toaster]:to-orange-50/95 group-[.toaster]:border-amber-200/60 group-[.toaster]:shadow-amber-100/30 dark:group-[.toaster]:from-amber-950/95 dark:group-[.toaster]:to-orange-950/95 dark:group-[.toaster]:border-amber-800/60 dark:group-[.toaster]:shadow-amber-900/30 [&>[data-icon]]:text-amber-600 dark:[&>[data-icon]]:text-amber-400 [&>[data-icon]]:bg-amber-100 dark:[&>[data-icon]]:bg-amber-900/50 [&>[data-icon]]:p-1.5 [&>[data-icon]]:rounded-lg',
  default:
    'group-[.toaster]:bg-gradient-to-r group-[.toaster]:from-gray-50/95 group-[.toaster]:to-slate-50/95 group-[.toaster]:border-gray-200/60 group-[.toaster]:shadow-gray-100/30 dark:group-[.toaster]:from-gray-950/95 dark:group-[.toaster]:to-slate-950/95 dark:group-[.toaster]:border-gray-800/60 dark:group-[.toaster]:shadow-gray-900/30 [&>[data-icon]]:text-gray-600 dark:[&>[data-icon]]:text-gray-400 [&>[data-icon]]:bg-gray-100 dark:[&>[data-icon]]:bg-gray-900/50 [&>[data-icon]]:p-1.5 [&>[data-icon]]:rounded-lg',
  actionButton:
    'bg-gray-900 text-white hover:bg-gray-800 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors duration-200 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-gray-200 font-tajawal',
  cancelButton:
    'bg-gray-100 text-gray-900 hover:bg-gray-200 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors duration-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700 font-tajawal',
  closeButton:
    'text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200 p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800',
};

const TOAST_STYLE = {
  fontSize: '14px',
  fontWeight: '500',
  lineHeight: '1.5',
  fontFamily:
    'Tajawal, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

const TOAST_CONFIG = {
  expand: false,
  richColors: true,
  position: 'bottom-left' as const,
  duration: 4000,
  closeButton: true,
};

const PERFORMANCE_CONFIG = {
  slowLoadThreshold: 500,
  interactiveDelay: 100,
};

// ===============================
// Lazy Loaded Components
// ===============================

const PermissionErrorDialog = lazyLoad(
  () =>
    import('@/components/PermissionErrorDialog').then((m) => ({
      default: m.PermissionErrorDialog,
    })),
  { errorBoundary: false },
);

const SharedDialog = lazyLoad(() => import('@/Shared'), {
  errorBoundary: true,
});

const SupervisorDialogsProvider = lazyLoad(
  () =>
    import('@/components/SupervisorDialogsProvider').then((m) => ({
      default: m.SupervisorDialogsProvider,
    })),
  { errorBoundary: false },
);

// ===============================
// Helper Functions
// ===============================

const initPerformanceTracking = () => {
  if (import.meta.env.MODE !== 'development') return;
  markPerformance('app-component-mount');
};

const getCurrentTheme = (): 'dark' | 'light' => {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
};

// ===============================
// Custom Hooks
// ===============================

const useRouteRestoration = (
  isInitialLoading: boolean,
  isDevelopment: boolean,
) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isInitialLoading) return;

    if (location.pathname !== '/') {
      sessionStorage.removeItem('redirectPath');
      sessionStorage.removeItem('spaRoute');
      sessionStorage.removeItem('hasRestoredRoute');
      return;
    }

    const redirectPath = sessionStorage.getItem('redirectPath');
    const spaRoute = sessionStorage.getItem('spaRoute');
    const targetPath = spaRoute || redirectPath;
    const hasRestoredRoute = sessionStorage.getItem('hasRestoredRoute');

    if (isDevelopment) {
    }

    if (targetPath && targetPath !== '/' && !hasRestoredRoute) {
      sessionStorage.removeItem('redirectPath');
      sessionStorage.removeItem('spaRoute');
      sessionStorage.setItem('hasRestoredRoute', 'true');

      if (isDevelopment) {
      }

      navigate(targetPath, { replace: true });
    }
  }, [navigate, location.pathname, isInitialLoading, isDevelopment]);
};

const usePerformanceTracking = (
  isInitialLoading: boolean,
  isFirstRender: boolean,
  setIsFirstRender: (value: boolean) => void,
  isDevelopment: boolean,
) => {
  const location = useLocation();
  const previousPathRef = useRef<string>(location.pathname);

  // Track initial render performance
  useEffect(() => {
    if (isInitialLoading) return;

    if (isFirstRender) {
      initPerformanceTracking();
      markPerformance('first-app-render');
      markPerformance('app-render-complete');

      if (isDevelopment) {
        try {
          const totalLoadTime = measurePerformance(
            'app-init',
            'app-render-complete',
            'app-total-load-time',
          );


          if (totalLoadTime > PERFORMANCE_CONFIG.slowLoadThreshold) {
            console.warn(
              `⚠️ Slow initial load detected: ${totalLoadTime.toFixed(2)}ms`,
            );
          }

          performance.clearMarks('app-init');
          performance.clearMarks('app-render-complete');
          performance.clearMeasures('app-total-load-time');
        } catch {
          console.debug('Could not measure initial load time');
        }
      }

      setIsFirstRender(false);
    }
  }, [
    isFirstRender,
    isDevelopment,
    location.pathname,
    isInitialLoading,
    setIsFirstRender,
  ]);

  // Track page interactive state
  useEffect(() => {
    if (isInitialLoading) return;

    const timer = setTimeout(() => {
      if (isDevelopment) {
        console.debug(`Page now fully loaded and interactive`);
      }
    }, PERFORMANCE_CONFIG.interactiveDelay);

    return () => clearTimeout(timer);
  }, [location.pathname, isDevelopment, isInitialLoading]);

  // Track navigation performance
  useEffect(() => {
    if (isInitialLoading || isFirstRender) return;

    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;

    previousPathRef.current = currentPath;

    if (currentPath === previousPath) return;

    if (isDevelopment) {
    }
  }, [location, isDevelopment, isFirstRender, isInitialLoading]);
};

const usePermissionErrorHandler = () => {
  const [permissionError, setPermissionError] = useState<any>(null);
  const [showPermissionDialog, setShowPermissionDialog] = useState(false);

  useEffect(() => {
    import('@/components/PermissionErrorDialog').then(
      ({ setPermissionErrorHandler }) => {
        setPermissionErrorHandler((data) => {
          setPermissionError(data);
          setShowPermissionDialog(true);
        });
      },
    );
  }, []);

  return {
    permissionError,
    showPermissionDialog,
    setShowPermissionDialog,
    setPermissionError,
  };
};

// ===============================
// Main Component
// ===============================

const App: FC = () => {
  const { isInitialLoading } = useInitialLoadingContext();
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
  const isDevelopment = import.meta.env.MODE === 'development';

  // Custom hooks
  const {
    permissionError,
    showPermissionDialog,
    setShowPermissionDialog,
    setPermissionError,
  } = usePermissionErrorHandler();

  useAriaHiddenFix();
  useRouteRestoration(isInitialLoading, isDevelopment);
  usePerformanceTracking(
    isInitialLoading,
    isFirstRender,
    setIsFirstRender,
    isDevelopment,
  );

  // Route handling
  const Content = useRoutes(router);

  const isAuthRoute = useMemo<boolean>(() => {
    return (
      window.location.pathname.includes('/login') ||
      sessionStorage.getItem('directLogout') === 'true'
    );
  }, []);

  const loadingFallback = useCallback(() => {
    return isAuthRoute ? null : <AppLoading />;
  }, [isAuthRoute]);

  // Early return during initial loading
  if (isInitialLoading) {
    return null;
  }

  const currentTheme = getCurrentTheme();

  return (
    <Suspense fallback={loadingFallback()}>
      <ErrorBoundary>
        <ConnectionProvider>
          <RtlProvider>
            <Toaster
              {...TOAST_CONFIG}
              theme={currentTheme}
              toastOptions={{
                style: TOAST_STYLE,
                classNames: {
                  toast: TOAST_CLASSES.base,
                  title: TOAST_CLASSES.title,
                  description: TOAST_CLASSES.description,
                  icon: TOAST_CLASSES.icon,
                  success: TOAST_CLASSES.success,
                  error: TOAST_CLASSES.error,
                  info: TOAST_CLASSES.info,
                  warning: TOAST_CLASSES.warning,
                  default: TOAST_CLASSES.default,
                  actionButton: TOAST_CLASSES.actionButton,
                  cancelButton: TOAST_CLASSES.cancelButton,
                  closeButton: TOAST_CLASSES.closeButton,
                },
              }}
            />

            <Suspense fallback={loadingFallback()}>
              <SharedDialog />
            </Suspense>

            <Suspense fallback={null}>
              <SupervisorDialogsProvider />
            </Suspense>

            {Content}

            {isDevelopment && <TestConnectionError />}

            <UpdatePrompt />
            <VersionCheck />
            <VersionNotification />

            {permissionError && (
              <PermissionErrorDialog
                open={showPermissionDialog}
                onClose={() => {
                  setShowPermissionDialog(false);
                  setPermissionError(null);
                }}
                errorData={permissionError}
              />
            )}
          </RtlProvider>
        </ConnectionProvider>
      </ErrorBoundary>
    </Suspense>
  );
};

export default memo(App);
